// src/shared/components/ui/card.tsx

import type { HTMLAttributes } from 'react'

import { cn } from '@/shared/utils/cn'

type CardPadding = 'none' | 'sm' | 'md' | 'lg'

const cardPaddingMap: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
}

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: CardPadding
  interactive?: boolean
}

export function Card({ className, padding = 'none', interactive, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-[var(--panel-border)] bg-[var(--panel)] shadow-sm',
        cardPaddingMap[padding],
        interactive &&
          'transition hover:border-[var(--panel-border-strong)] hover:shadow-md focus-within:ring-2 focus-within:ring-[var(--color-brand)]',
        className,
      )}
      {...props}
    />
  )
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('border-b border-[var(--panel-border)] p-4', className)} {...props} />
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-4', className)} {...props} />
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('font-display text-base font-semibold text-[var(--color-text)]', className)}
      {...props}
    />
  )
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('mt-1 text-sm text-[var(--color-text-soft)]', className)} {...props} />
}
